import { Box, Typography, Stack, alpha, useTheme, Button, Paper } from "@mui/material";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { LuBuilding, LuCopy, LuRefreshCcw, LuLayoutTemplate } from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { fetchWithToken } from "../../authentication/AuthProvider";
import ColumnVisibilityButton from "../../components/button/ColumnVisibilityButton";
import RefreshButton from "../../components/button/RefreshButton";
import { MainContainer } from "../../components/container/MainContainer";
import ColumnMenu from "../../components/context-menu/ColumnMenu";
import ContextMenu from "../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { centerAlignColDef, defaultColDef, floatFormater, leftAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import config from "../../config";
import { EnvFactor } from "../../db_access/env_factor";
import { Process } from "../../db_access/process";
import CarbonImpactSummary from "../../environment-page-components/CarbonImpactSummary";
import Footprint from "../../environment-page-components/Footprint";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
import { getOrgTokens } from "../../theme/style/organizationPalette";

const BuildingPreBuiltTemplate = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const tok = getOrgTokens(isDark);
    const { selectedLanguage, columnVisibilities, enqueueAlert } = useContextProvider();
    
    const pageType = "utilizer";
    const [buildings, setBuildings] = useState([]);
    const [selectedBuildings, setSelectedBuildings] = useState([]);
    const [allEnvFactorRows, setAllEnvFactorRows] = useState([]);
    const [processData, setProcessData] = useState([]);
    const [refresh, setRefresh] = useState(false);
    const [searchText, setSearchText] = useState("");
    
    const gridRef = useRef(null);
    const gridHeaderContainerRef = useRef(null);
    const colVisibility_key = "building_prebuilt_template";
    const defaultCols = ["name", "location", "gwp", "biogenic_gwp"];
    const [columnMenuVisible, setColumnMenuVisible] = useState(false);
    const [menuVisible, setMenuVisible] = useState(false);
    const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

    const activeColor = '#7c3aed'; // Building Purple

    useEffect(() => {
        const url = config.building.get_from_admin;
        fetchWithToken(url, { ...config.method.POST })
            .then((res) => res.json())
            .then(async (data) => {
                const buildingsWithGWP = await Promise.all(
                    data.map(async (building) => {
                        try {
                            const res = await fetchWithToken(config.building.root + "/get_results", {
                                ...config.method.POST,
                                headers: config.header.json,
                                body: JSON.stringify({ building_id: building.id }),
                            });
                            const resData = await res.json();
                            const embodied = resData?.embodiedCarbon ?? 0;
                            const operational = resData?.operationalCarbon ?? 0;
                            const biogenic = resData?.biogenicCarbon ?? 0;
                            return {
                                ...building,
                                gwp: embodied + operational,
                                biogenic_gwp: biogenic,
                            };
                        } catch (e) {
                            return { ...building, gwp: 0, biogenic_gwp: 0 };
                        }
                    })
                );
                setBuildings(buildingsWithGWP);
            })
            .catch((err) => console.error("Error fetching buildings:", err));
    }, [refresh]);

    useEffect(() => {
        if (selectedBuildings.length === 0) {
            setProcessData([]);
            setAllEnvFactorRows([]);
        } else {
            Process.get_by_building_ids(selectedBuildings.map((i) => i.id))
                .then((res) => res.json())
                .then((data) => setProcessData(data));
        }
    }, [selectedBuildings]);

    useEffect(() => {
        if (processData.length === 0) {
            setAllEnvFactorRows([]);
        } else {
            EnvFactor.get_by_process_ids(processData.map((i) => i.id))
                .then((res) => res.json())
                .then((data) => setAllEnvFactorRows(data));
        }
    }, [processData]);

    const duplicateToMyWorkspace = useCallback((selectedItems) => {
        const url = config.building.duplicate_from_template;
        fetchWithToken(url, {
            ...config.method.POST,
            headers: config.header.json,
            body: JSON.stringify({ ids: selectedItems.map((s) => s.id) }),
        }).then(() => {
            gridRef.current?.api?.deselectAll();
            enqueueAlert({ message: t("Building templates duplicated"), severity: "success" });
            setRefresh((r) => !r);
        }).catch(() => enqueueAlert({ message: t("Failed to duplicate building"), severity: "error" }));
    }, [t, enqueueAlert]);

    const colDefs = useMemo(() => {
        const visibleCols = columnVisibilities[colVisibility_key] || defaultCols;
        const isHidden = (field) => !visibleCols.includes(field);

        return [
            { 
                headerName: t("Name"), 
                field: "name", 
                ...defaultColDef, 
                ...centerAlignColDef, 
                flex: 2,
                cellStyle: { ...tok.gridCellText, fontWeight: 500 } 
            },
            { headerName: t("Location"), field: "location", ...defaultColDef, ...centerAlignColDef, flex: 1 },
            { headerName: t("GWP (kg CO₂e)"), field: "gwp", ...defaultColDef, ...centerAlignColDef, ...floatFormater, width: 160 },
            { headerName: t("Biogenic GWP (kg CO₂e)"), field: "biogenic_gwp", ...defaultColDef, ...centerAlignColDef, ...floatFormater, width: 180 },
        ].map((colDef) => ({ 
            ...colDef, 
            hide: isHidden(colDef.field),
            headerClass: 'ag-header-cell-centered'
        }));
    }, [columnVisibilities, t, tok.gridCellText]);

    const contextMenuOptions = useMemo(() => [
        { label: t("Duplicate to my workspace"), action: duplicateToMyWorkspace, needSelectedItems: true }
    ], [duplicateToMyWorkspace, t]);

    return (
        <MainContainer>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                
                {/* Standardized Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ 
                            width: 52, height: 52, borderRadius: tok.radius.button,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', 
                            bgcolor: alpha(activeColor, 0.1), color: activeColor 
                        }}>
                            <LuBuilding size={28} />
                        </Box>
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: tok.text.primary, lineHeight: 1.2 }}>
                                {t("Building Templates")}
                            </Typography>
                            <Typography sx={{ ...tok.gridCellText, color: tok.text.muted }}>
                                {buildings.length} {t("ready-to-use building models")}
                            </Typography>
                        </Box>
                    </Stack>

                    {selectedBuildings.length > 0 && (
                        <Button
                            variant="contained"
                            startIcon={<LuCopy size={18} />}
                            onClick={() => duplicateToMyWorkspace(selectedBuildings)}
                            sx={{
                                bgcolor: activeColor,
                                '&:hover': { bgcolor: alpha(activeColor, 0.9) },
                                borderRadius: tok.radius.button,
                                fontWeight: 700, textTransform: 'none', px: 3
                            }}
                        >
                            {t("Duplicate to Workspace")} ({selectedBuildings.length})
                        </Button>
                    )}
                </Box>

                {/* Library Section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" ref={gridHeaderContainerRef}>
                        <Box sx={{ flex: 1 }}>
                            <SearchBox value={searchText} onChange={setSearchText} placeholder={t("Search building library...")} />
                        </Box>
                        <ExportAsXLSXButton gridRef={gridRef} filename="building_templates" />
                        <ColumnVisibilityButton
                            columns={colDefs}
                            visibleColumns={columnVisibilities[colVisibility_key] || defaultCols}
                            columnVis_key={colVisibility_key}
                        />
                        <RefreshButton onRefresh={() => setRefresh((r) => !r)} />
                    </Stack>

                    <AgGrid
                        rowData={buildings}
                        columnDefs={colDefs}
                        onSelectionChanged={(e) => setSelectedBuildings(e.api.getSelectedRows())}
                        handleContextMenu={(e) => {
                            e.preventDefault(); setMenuPosition({ x: e.pageX, y: e.pageY }); setMenuVisible(true);
                        }}
                        quickFilterText={searchText}
                        ref={gridRef}
                        domLayout="autoHeight"
                    />
                </Box>

                {/* Analytical Deep Dive */}
                {selectedBuildings.length === 1 && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: tok.text.primary, textAlign: 'left', pl: 1, borderLeft: `4px solid ${activeColor}` }}>
                            {t("Template Analysis")}: {selectedBuildings[0].name}
                        </Typography>
                        
                        <SystemBoundary
                            processData={processData}
                            selectedLanguage={selectedLanguage}
                            isBuilding={true}
                            expanded={true}
                        />
                        <CarbonImpactSummary
                            selectedRow={selectedBuildings[0]}
                            selectedLanguage={selectedLanguage}
                            expanded={true}
                        />
                        <Footprint
                            processData={processData}
                            selectedLanguage={selectedLanguage}
                            pageType={pageType}
                            expanded={true}
                        />
                        <SankeyDiagram
                            processData={processData}
                            selectedLanguage={selectedLanguage}
                            selectedRow={selectedBuildings[0]}
                            allEnvFactorRows={allEnvFactorRows}
                            expanded={true}
                        />
                    </Box>
                )}
            </Box>

            <ColumnMenu
                anchorRef={gridHeaderContainerRef}
                visible={columnMenuVisible}
                onClose={() => setColumnMenuVisible(false)}
                colDefs={colDefs}
                colVisibility_key={colVisibility_key}
                defaultCols={defaultCols}
            />

            <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={() => setMenuVisible(false)} selectedItems={selectedBuildings} options={contextMenuOptions} />
        </MainContainer>
    );
};

export default BuildingPreBuiltTemplate;
